import { AlertCircle, CheckCircle2, FileText, Image as ImageIcon, UploadCloud, X } from "lucide-react";
import PropTypes from "prop-types";
import classnames from "classnames";
import LoadingButton from "@components/ui-components/loading-button";
import { formatFileSize } from "@helpers/utils";
import { PREVIEW_KIND, UPLOAD_STATUS } from "./constants";
import useFileUploader from "./use-file-uploader";

// The app's single file picker: click or drop to stage, review what was picked,
// then upload. Nothing is sent until the confirm button is pressed, so a
// mis-dropped file costs a removal rather than a stored object.
//
// `onUploaded(files)` receives [{ url, name, originalName, label, size }] once the
// uploads service has taken them, and is where a feature attaches them to its
// own record. Returning a promise holds the uploader in its loading state
// until that attach settles.

const KIND_ICON = {
    [PREVIEW_KIND.PDF]  : FileText,
    [PREVIEW_KIND.IMAGE]: ImageIcon,
    [PREVIEW_KIND.FILE] : FileText
};

const STATUS_NOTE = {
    [UPLOAD_STATUS.STAGED]   : "Ready to upload",
    [UPLOAD_STATUS.UPLOADING]: "Uploading…",
    [UPLOAD_STATUS.DONE]     : "Uploaded",
    [UPLOAD_STATUS.ERROR]    : "Failed"
};

const StagedRow = ({ item, uploading, requireName, namePlaceholder, onRemove, onLabelChange }) => {
    const Icon = KIND_ICON[item.kind] || FileText;
    const isUploading = item.status === UPLOAD_STATUS.UPLOADING;
    const isDone = item.status === UPLOAD_STATUS.DONE;
    const isError = item.status === UPLOAD_STATUS.ERROR;

    return (
        <li className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
            {item.previewUrl ? (
                <img src={item.previewUrl} alt={item.name} className="h-10 w-10 shrink-0 rounded-lg border border-gray-200 object-cover" />
            ) : (
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-light text-muted">
                    <Icon className="h-4 w-4" />
                </span>
            )}

            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900" title={item.name}>{item.name}</p>
                <p className={classnames("mt-0.5 text-xs", isError ? "text-danger" : "text-muted")}>
                    {formatFileSize(item.size)} · {isError ? item.error || STATUS_NOTE[item.status] : STATUS_NOTE[item.status]}
                </p>
                {requireName && (
                    <input
                        type="text"
                        value={item.label}
                        onChange={(e) => onLabelChange(item.id, e.target.value)}
                        disabled={uploading || isDone}
                        placeholder={namePlaceholder}
                        aria-label={`Name for ${item.name}`}
                        className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-900 placeholder:text-muted focus:border-primary focus:outline-none disabled:cursor-not-allowed disabled:bg-light" />
                )}
            </div>

            {isDone && <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />}
            {isError && <AlertCircle className="h-4 w-4 shrink-0 text-danger" />}

            {!isUploading && !isDone && (
                <button
                    type="button"
                    onClick={() => onRemove(item.id)}
                    disabled={uploading}
                    aria-label={`Remove ${item.name}`}
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted transition-colors hover:bg-light hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50">
                    <X className="h-4 w-4" />
                </button>
            )}
        </li>
    );
};

StagedRow.propTypes = {
    item           : PropTypes.object.isRequired,
    uploading      : PropTypes.bool,
    requireName    : PropTypes.bool,
    namePlaceholder: PropTypes.string,
    onRemove       : PropTypes.func.isRequired,
    onLabelChange  : PropTypes.func.isRequired
};

const FileUploader = (props) => {
    const {
        uploadType,
        onUploaded,
        multiple = true,
        mimeTypes = [],
        extensions = [],
        maxSizeMb,
        accept = "",
        title = "Drag & drop files here, or click to browse",
        note = "",
        // Names what is being uploaded in the confirm button, e.g. "Upload
        // document" / "Upload 3 documents".
        itemNoun = "file",
        // Asks for a name per staged file and holds Upload until each has one.
        requireName = false,
        namePlaceholder = "Name",
        disabled = false,
        disabledNote = "",
        className = "",
        "data-cy": dataCy = "file-uploader"
    } = props;

    const {
        items,
        uploading,
        dragActive,
        hasStaged,
        isMissingName,
        openPicker,
        removeItem,
        setItemLabel,
        clear,
        upload,
        inputProps,
        dropProps
    } = useFileUploader({ uploadType, multiple, mimeTypes, extensions, maxSizeMb, onUploaded, disabled, requireName });

    const pendingCount = items.filter((item) => item.status !== UPLOAD_STATUS.DONE).length;

    return (
        <div className={className} data-cy={dataCy}>
            {/* The whole zone is the picker — clicking it opens the file dialog.
                The hidden input is a sibling rather than a child: a <button>
                cannot contain a form control. */}
            <button
                {...dropProps}
                type="button"
                disabled={disabled || uploading}
                onClick={openPicker}
                data-cy={`${dataCy}-dropzone`}
                className={classnames(
                    "flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors disabled:cursor-not-allowed disabled:opacity-60",
                    dragActive ? "border-primary/50 bg-light" : "border-gray-200 hover:border-primary/40 hover:bg-light"
                )}>
                <UploadCloud className={classnames("h-7 w-7", dragActive ? "text-primary" : "text-muted")} />
                <p className="mt-2.5 text-sm font-medium text-gray-900">{title}</p>
                {(disabled ? disabledNote || note : note) && (
                    <p className="mt-1 text-xs text-muted">{disabled ? disabledNote || note : note}</p>
                )}
            </button>
            <input {...inputProps} accept={accept} className="hidden" />

            {hasStaged && (
                <>
                    <ul className="mt-3 space-y-2">
                        {items.map((item) => (
                            <StagedRow
                                key={item.id}
                                item={item}
                                uploading={uploading}
                                requireName={requireName}
                                namePlaceholder={namePlaceholder}
                                onRemove={removeItem}
                                onLabelChange={setItemLabel} />
                        ))}
                    </ul>

                    <div className="mt-3 flex items-center justify-end gap-2">
                        <LoadingButton
                            color="neutral"
                            size="sm"
                            text="Clear"
                            onClick={clear}
                            disabled={uploading}
                            testId={`${dataCy}-clear`} />
                        <LoadingButton
                            size="sm"
                            icon={UploadCloud}
                            text={pendingCount > 1 ? `Upload ${pendingCount} ${itemNoun}s` : `Upload ${itemNoun}`}
                            loadingText="Uploading…"
                            loading={uploading}
                            disabled={pendingCount === 0 || isMissingName}
                            onClick={upload}
                            testId={`${dataCy}-confirm`} />
                    </div>
                </>
            )}
        </div>
    );
};

FileUploader.propTypes = {
    // Storage folder the uploads service files these under, e.g. "kyc_doc".
    uploadType     : PropTypes.string.isRequired,
    // Receives [{ url, name, originalName, label, size }]; may return a promise.
    // `label` is the user-entered name when `requireName` is set.
    onUploaded     : PropTypes.func.isRequired,
    multiple       : PropTypes.bool,
    // Accepted MIME types and extensions — both are checked, because scanned
    // documents frequently arrive with an empty or generic `file.type`.
    mimeTypes      : PropTypes.arrayOf(PropTypes.string),
    extensions     : PropTypes.arrayOf(PropTypes.string),
    maxSizeMb      : PropTypes.number,
    // The input's own `accept` attribute, which only filters the file dialog.
    accept         : PropTypes.string,
    title          : PropTypes.string,
    note           : PropTypes.string,
    itemNoun       : PropTypes.string,
    requireName    : PropTypes.bool,
    namePlaceholder: PropTypes.string,
    disabled       : PropTypes.bool,
    // Replaces `note` while disabled, so the zone says why it is dead.
    disabledNote   : PropTypes.string,
    className      : PropTypes.string,
    "data-cy"      : PropTypes.string
};

export default FileUploader;
